import "../css/app.css";
import GLightbox from "glightbox";
import "glightbox/dist/css/glightbox.css";
import Swiper from "swiper/bundle";
import { EffectCoverflow, Autoplay, Scrollbar } from "swiper/modules";

document.addEventListener("DOMContentLoaded", () => {
  // ── Site intro ────────────────────────────────────────────────────
  const introEl = document.getElementById("site-intro");
  if (introEl) {
    const introLogo  = document.getElementById("intro-logo");
    const introVideo = introEl.querySelector("video");
    const LW = 105, LH = 16.7579; // logo viewBox dimensions

    const setTransform = (s) => {
      const vw = window.innerWidth, vh = window.innerHeight;
      introLogo.setAttribute(
        "transform",
        `translate(${vw / 2 - (LW / 2) * s},${vh / 2 - (LH / 2) * s}) scale(${s})`
      );
    };

    const sStart = () => Math.max(window.innerWidth * 0.2, 160) / LW;
    const sEnd   = () =>
      Math.max(window.innerWidth / LW, window.innerHeight / LH) * 3.2;

    setTransform(sStart());

    let startTime = null;
    const DELAY = 600, DURATION = 2400;
    const easeIn = (t) => t ** 3.5;

    const tick = (now) => {
      if (!startTime) startTime = now;
      const elapsed = now - startTime - DELAY;
      if (elapsed < 0) { requestAnimationFrame(tick); return; }
      const t = Math.min(elapsed / DURATION, 1);
      setTransform(sStart() + (sEnd() - sStart()) * easeIn(t));
      if (t < 1) {
        requestAnimationFrame(tick);
      } else {
        introEl.style.transition = "opacity 0.5s ease";
        introEl.style.opacity = "0";
        document.body.style.overflow = "";
        setTimeout(() => {
          introEl.remove();
          sessionStorage.setItem("introShown", "1");
        }, 500);
      }
    };

    document.body.style.overflow = "hidden";
    if (introVideo) introVideo.play().catch(() => {});
    requestAnimationFrame(tick);
  }

  let lightbox = null;
  if (document.querySelector(".glightbox")) {
    lightbox = GLightbox({
      selector: ".glightbox",
      touchNavigation: true,
      loop: true,
      closeOnOutsideClick: true,
      slideHTML: `<div class="gslide">
        <div class="gslide-inner-content">
          <div class="ginner-container">
            <div class="gslide-media"></div>
          </div>
        </div>
      </div>`,
    });
  }

  // Shop filter tabs
  document.querySelectorAll(".shop-tabs").forEach((tabGroup) => {
    const grid = tabGroup.closest("section")?.querySelector(".grid");
    tabGroup.querySelectorAll(".shop-tab").forEach((tab) => {
      tab.addEventListener("click", () => {
        tabGroup
          .querySelectorAll(".shop-tab")
          .forEach((t) => t.classList.remove("shop-tab-active"));
        tab.classList.add("shop-tab-active");

        const slug = tab.dataset.collection;
        grid?.querySelectorAll("article[data-collection]").forEach((card) => {
          const cols = card.dataset.collection.split(" ");
          card.style.display = !slug || cols.includes(slug) ? "" : "none";
        });
      });
    });
  });

  // Gallery grid — AJAX load more
  document.querySelectorAll(".gallery-load-more").forEach((btn) => {
    const grid = btn.closest("section")?.querySelector(".gallery-grid");
    const config = window.andraGallery;

    if (!grid || !config?.ajaxUrl) return;

    btn.addEventListener("click", async () => {
      if (btn.disabled) return;

      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");

      const formData = new FormData();
      formData.append("action", config.action);
      formData.append("nonce", config.nonce);
      formData.append("page", btn.dataset.page || "2");

      try {
        const res = await fetch(config.ajaxUrl, {
          method: "POST",
          body: formData,
          credentials: "same-origin",
        });
        const json = await res.json();

        if (!json.success || !json.data?.html) {
          throw new Error("Gallery load failed");
        }

        grid.insertAdjacentHTML("beforeend", json.data.html);

        if (json.data.has_more) {
          btn.dataset.page = String(json.data.next_page);
          btn.disabled = false;
          btn.setAttribute("aria-busy", "false");
        } else {
          btn.closest(".flex")?.remove();
        }
      } catch {
        btn.disabled = false;
        btn.setAttribute("aria-busy", "false");
      }
    });
  });

  // Music grid — AJAX load more
  document.querySelectorAll(".music-load-more").forEach((btn) => {
    const grid = btn.closest("section")?.querySelector(".music-grid");
    const config = window.andraMusic;

    if (!grid || !config?.ajaxUrl) return;

    btn.addEventListener("click", async () => {
      if (btn.disabled) return;

      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");

      const formData = new FormData();
      formData.append("action", config.action);
      formData.append("nonce", config.nonce);
      formData.append("offset", btn.dataset.offset || String(1 + 12));

      try {
        const res = await fetch(config.ajaxUrl, {
          method: "POST",
          body: formData,
          credentials: "same-origin",
        });
        const json = await res.json();

        if (!json.success || !json.data?.html) {
          throw new Error("Music load failed");
        }

        grid.insertAdjacentHTML("beforeend", json.data.html);
        lightbox?.reload();

        const activeFilter =
          btn.closest("section")?.querySelector(".submenu-tab-active")?.dataset
            .filter ?? "";
        if (activeFilter) {
          grid.querySelectorAll(".music-card").forEach((card) => {
            const types = card.dataset.types.split(" ");
            card.style.display = types.includes(activeFilter) ? "" : "none";
          });
        }

        if (json.data.has_more) {
          btn.dataset.offset = String(json.data.next_offset);
          btn.disabled = false;
          btn.setAttribute("aria-busy", "false");
        } else {
          btn.closest(".flex")?.remove();
        }
      } catch {
        btn.disabled = false;
        btn.setAttribute("aria-busy", "false");
      }
    });
  });

  // Podcast grid — AJAX load more
  document.querySelectorAll(".podcast-load-more").forEach((btn) => {
    const grid = btn.closest("section")?.querySelector(".podcast-grid");
    const config = window.andraPodcast;

    if (!grid || !config?.ajaxUrl) return;

    btn.addEventListener("click", async () => {
      if (btn.disabled) return;

      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");

      const formData = new FormData();
      formData.append("action", config.action);
      formData.append("nonce", config.nonce);
      formData.append("offset", btn.dataset.offset || String(1 + 11));

      try {
        const res = await fetch(config.ajaxUrl, {
          method: "POST",
          body: formData,
          credentials: "same-origin",
        });
        const json = await res.json();

        if (!json.success || !json.data?.html) {
          throw new Error("Podcast load failed");
        }

        grid.insertAdjacentHTML("beforeend", json.data.html);
        lightbox?.reload();

        if (json.data.has_more) {
          btn.dataset.offset = String(json.data.next_offset);
          btn.disabled = false;
          btn.setAttribute("aria-busy", "false");
        } else {
          btn.closest(".flex")?.remove();
        }
      } catch {
        btn.disabled = false;
        btn.setAttribute("aria-busy", "false");
      }
    });
  });

  // Music type filter tabs
  document.querySelectorAll(".submenu-tabs").forEach((tabGroup) => {
    const grid = tabGroup.closest("section")?.querySelector(".music-grid");
    tabGroup.querySelectorAll(".submenu-tab").forEach((tab) => {
      tab.addEventListener("click", () => {
        tabGroup
          .querySelectorAll(".submenu-tab")
          .forEach((t) => t.classList.remove("submenu-tab-active"));
        tab.classList.add("submenu-tab-active");

        const slug = tab.dataset.filter;
        grid?.querySelectorAll(".music-card").forEach((card) => {
          const types = card.dataset.types.split(" ");
          card.style.display = !slug || types.includes(slug) ? "" : "none";
        });
      });
    });
  });

  document.querySelectorAll(".music-swiper").forEach((el) => {
    const wrap = el.closest(".music-slider-wrap");
    const scrollbarEl = wrap?.querySelector(".swiper-scrollbar") ?? null;

    const swiper = new Swiper(el, {
      modules: [EffectCoverflow, Autoplay, Scrollbar],
      effect: "coverflow",
      centeredSlides: true,
      slidesPerView: "auto",
      loop: true,
      speed: 600,
      grabCursor: true,
      autoplay: {
        delay: 6000,
        disableOnInteraction: false,
      },
      scrollbar: {
        el: scrollbarEl,
        draggable: true,
        snapOnRelease: true,
      },
      preventClicks: false,
      preventClicksPropagation: false,
      breakpoints: {
        768: {
          coverflowEffect: {
            rotate: 0,
            stretch: -120,
            depth: 200,
            modifier: 1,
            slideShadows: false,
          },
        },
      },
    });

  });

  // Concert share popup
  const sharePopup = document.getElementById("concert-share-popup");
  const shareClose = document.getElementById("concert-share-close");
  const shareFacebook = document.getElementById("share-facebook");
  const shareX = document.getElementById("share-x");
  const shareThreads = document.getElementById("share-threads");
  const shareLinkedin = document.getElementById("share-linkedin");
  const shareEmail = document.getElementById("share-email");

  function openSharePopup(title, url) {
    const enc = encodeURIComponent;
    shareFacebook.href = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
    shareX.href = `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`;
    shareThreads.href = `https://www.threads.net/intent/post?text=${enc(title + " " + url)}`;
    shareLinkedin.href = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;
    shareEmail.href = `mailto:?subject=${enc(title)}&body=${enc(title + "\n" + url)}`;
    sharePopup.style.display = "flex";
    document.body.style.overflow = "hidden";
  }

  function closeSharePopup() {
    sharePopup.style.display = "none";
    document.body.style.overflow = "";
  }

  if (sharePopup) {
    document.querySelectorAll(".concert-share-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const parts = [btn.dataset.title];
        if (btn.dataset.date) parts.push(btn.dataset.date);
        if (btn.dataset.time) parts.push("ora " + btn.dataset.time);
        if (btn.dataset.city) parts.push(btn.dataset.city);
        if (btn.dataset.location) parts.push(btn.dataset.location);
        openSharePopup(parts.join(" · "), btn.dataset.url);
      });
    });

    shareClose?.addEventListener("click", closeSharePopup);

    sharePopup.addEventListener("click", (e) => {
      if (e.target === sharePopup) closeSharePopup();
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeSharePopup();
    });
  }

  // Mobile menu
  const mobileMenu = document.getElementById("mobile-menu");
  const menuToggle = document.getElementById("mobile-menu-toggle");
  const menuClose = document.getElementById("mobile-menu-close");

  function openMobileMenu() {
    mobileMenu?.classList.add("is-open");
    mobileMenu?.setAttribute("aria-hidden", "false");
    menuToggle?.setAttribute("aria-expanded", "true");
    document.body.classList.add("mobile-menu-open");
    document.body.style.overflow = "hidden";
  }

  function closeMobileMenu() {
    mobileMenu?.classList.remove("is-open");
    mobileMenu?.setAttribute("aria-hidden", "true");
    menuToggle?.setAttribute("aria-expanded", "false");
    document.body.classList.remove("mobile-menu-open");
    document.body.style.overflow = "";
  }

  menuToggle?.addEventListener("click", openMobileMenu);
  menuClose?.addEventListener("click", closeMobileMenu);

  mobileMenu
    ?.querySelectorAll(".mobile-nav-link, .mobile-menu-close-trigger")
    .forEach((el) => {
      el.addEventListener("click", closeMobileMenu);
    });

  document.addEventListener("keydown", (e) => {
    if (
      e.key === "Escape" &&
      document.body.classList.contains("mobile-menu-open")
    ) {
      closeMobileMenu();
    }
  });

  // Content-row scrollable info box
  document.querySelectorAll(".content-info-wrapper").forEach((wrapper) => {
    const box = wrapper.querySelector(".content-info");
    const fade = wrapper.querySelector(".content-info-fade");
    const arrows = wrapper.querySelector(".content-info-arrows");
    const btnUp = wrapper.querySelector(".content-info-up");
    const btnDown = wrapper.querySelector(".content-info-down");
    const step = 150;

    const updateArrows = () => {
      const atBottom = box.scrollTop + box.clientHeight >= box.scrollHeight - 1;
      btnUp.disabled = box.scrollTop <= 0;
      btnDown.disabled = atBottom;
      fade?.classList.toggle("hidden", atBottom);
    };

    if (box.scrollHeight > box.clientHeight) {
      arrows.classList.remove("hidden");
      arrows.classList.add("flex");
      fade?.classList.remove("hidden");
      updateArrows();
    }

    btnUp.addEventListener("click", () =>
      box.scrollBy({ top: -step, behavior: "smooth" }),
    );
    btnDown.addEventListener("click", () =>
      box.scrollBy({ top: step, behavior: "smooth" }),
    );
    box.addEventListener("scroll", updateArrows);
  });

  document.querySelectorAll(".gallery-swiper").forEach((el) => {
    const scrollbarEl =
      el.closest(".gallery-slider-clip")?.querySelector(".swiper-scrollbar") ??
      null;
    new Swiper(el, {
      slidesPerView: "auto",
      spaceBetween: 16,
      loop: true,
      speed: 600,
      grabCursor: true,
      slidesOffsetBefore: 14,
      slidesOffsetAfter: 7,
      centeredSlides: true,
      centerInsufficientSlides: true,
      scrollbar: {
        el: scrollbarEl,
        draggable: true,
        snapOnRelease: true,
      },
    });
  });
});
